import React from 'react';
import { Card, Text } from '@perch-labs/shrike';
import { copyFor, routes } from 'config';
import { List } from 'styles';
import { logout } from 'utils/logout';
import { useContractAgreements } from 'hooks/use-contract-agreements.hook';
import { useUser } from 'hooks/use-user.hook';
import { ErrorModule } from 'components/errors';
import { LoadingModule } from 'components/loading';
import { StandardRow } from 'components/standard-row';
import { CardContent, CardHeader, Button } from 'pages/settings/settings.style';
import { useAccountContext } from 'contexts/account';

const getCopy = copyFor('settings.userSettings');
const { contracts, personalInformation, languagePreference } = routes();
interface Props {
  children?: React.ReactNode;
}

export const UserSettingsBase = ({ children }: Props) => {
  const user = useUser();
  const contractAgreements = useContractAgreements({
    excludeAddendums: true,
  });
  const { accounts } = useAccountContext();
  const multiAccount = accounts?.length > 1;

  if (contractAgreements.loading || user.loading) return <LoadingModule />;
  if (contractAgreements.error || user.error)
    return <ErrorModule title={getCopy('title')} />;

  const numberOfContractAgreements = contractAgreements.data?.length || 0;
  const name = user.data?.name;
  const language =
    user.data?.languageShortCode === 'es'
      ? getCopy('languagePreference.es')
      : getCopy('languagePreference.en');

  return (
    <Card mobileVariant="flat">
      <CardHeader>
        <Text tag="h2" textStyle="heading700" margin={{ bottom: '16px' }}>
          {getCopy('title')}
        </Text>
        {multiAccount && (
          <Text margin={{ bottom: '16px' }}>{getCopy('description')}</Text>
        )}
      </CardHeader>
      <CardContent>
        <List margin="0">
          <StandardRow
            iconLinkProps={{ to: personalInformation }}
            includesBuffer={true}
            heading={getCopy('personalInformation.heading')}
            headingTag="h3"
            text={getCopy('personalInformation.copy', { name })}
            bufferWidth="32px"
            mobileBufferWidth="32px"
            notranslate={true}
          />
          <StandardRow
            iconLinkProps={{
              ariaLabel: getCopy('languagePreference.heading'),
              to: languagePreference,
            }}
            includesBuffer={true}
            heading={getCopy('languagePreference.heading')}
            headingTag="h3"
            text={getCopy('languagePreference.copy', { language })}
            bufferWidth="32px"
            mobileBufferWidth="32px"
          />

          <StandardRow
            hasBottomBorder={!children}
            iconLinkProps={{
              ariaLabel: getCopy('contractAgreements.heading'),
              to: contracts,
            }}
            includesBuffer={true}
            heading={getCopy('contractAgreements.heading')}
            headingTag="h3"
            text={getCopy('contractAgreements.subHeading', {
              numberOfContractAgreements,
            })}
            bufferWidth="32px"
            mobileBufferWidth="32px"
          />
          {children}
        </List>
        <Button
          backgroundless={true}
          margin={{ left: '24px', top: '32px' }}
          onClick={logout as (e: React.MouseEvent<HTMLButtonElement>) => void}
        >
          {getCopy('buttons.logout')}
        </Button>
      </CardContent>
    </Card>
  );
};
